import React, { useState } from "react";
import { Heart, Pin, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * Compact icon-button trio for toggling favorite, pin, and archive on any list item.
 *
 * Props:
 *   type        — "note" | "reminder" | "document" | "password" | "photo" | "video" | "journal"
 *   id          — item id
 *   isFavorite  — current favorite state (boolean)
 *   pinnedAt    — current pinned_at ISO string OR null
 *   isArchived  — current archived state (boolean)
 *   showArchive — include archive icon (default: true)
 *   onChanged   — callback: ({ is_favorite, pinned_at, is_archived }) => void
 *                (fields present only when they changed)
 *   onArchived  — callback fired specifically when item was archived (for parent to remove from list)
 *   size        — "sm" | "md" (default "md")
 *   variant     — "row" | "overlay"
 */
export default function FavPinButtons({
  type,
  id,
  isFavorite = false,
  pinnedAt = null,
  isArchived = false,
  showArchive = true,
  onChanged,
  onArchived,
  size = "md",
  variant = "row",
  className,
}) {
  const [busy, setBusy] = useState(false);

  const isPinned = !!pinnedAt;
  const btnSize = size === "sm" ? "h-7 w-7" : "h-8 w-8";
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  const overlay =
    variant === "overlay"
      ? "bg-black/50 backdrop-blur text-white hover:bg-black/70 border border-white/10"
      : "hover:bg-muted text-muted-foreground";

  const activeFav = isFavorite ? "text-rose-500 hover:text-rose-500" : "";
  const activePin = isPinned ? "text-primary hover:text-primary" : "";
  const activeArch = isArchived ? "text-amber-500 hover:text-amber-500" : "";

  const doAction = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === "fav") {
        const next = !isFavorite;
        const { data } = await api.post("/favorites/toggle", { type, id, is_favorite: next });
        onChanged?.({ is_favorite: data.is_favorite });
        toast.success(next ? "Added to Favorites" : "Removed from Favorites");
      } else if (kind === "pin") {
        const next = !isPinned;
        const { data } = await api.post("/pins/toggle", { type, id, pinned: next });
        onChanged?.({ pinned_at: data.pinned_at });
        toast.success(next ? "Pinned to Home" : "Unpinned from Home");
      } else if (kind === "archive") {
        const next = !isArchived;
        const { data } = await api.post("/archive/toggle", { type, id, archived: next });
        onChanged?.({ is_archived: data.is_archived, archived_at: data.archived_at });
        if (next) onArchived?.(id);
        toast.success(next ? "Moved to Archive" : "Restored from Archive");
      }
    } catch {
      toast.error("Couldn't update — try again");
    } finally {
      setBusy(false);
    }
  };

  const IconButton = ({ kind, label, ActiveIcon, Icon, isActive, activeCls, testId }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); doAction(kind); }}
      disabled={busy}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center justify-center rounded-full transition active:scale-95",
        btnSize,
        overlay,
        activeCls,
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      )}
      data-testid={testId}
    >
      {isActive && ActiveIcon ? (
        <ActiveIcon className={cn(iconSize, "fill-current")} strokeWidth={2} />
      ) : (
        <Icon className={iconSize} strokeWidth={2} />
      )}
    </button>
  );

  return (
    <div className={cn("inline-flex items-center gap-1", className)} data-testid={`fav-pin-${type}-${id}`}>
      <IconButton
        kind="fav"
        label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        Icon={Heart}
        ActiveIcon={Heart}
        isActive={isFavorite}
        activeCls={activeFav}
        testId={`fav-toggle-${type}-${id}`}
      />
      <IconButton
        kind="pin"
        label={isPinned ? "Unpin from Home" : "Pin to Home"}
        Icon={Pin}
        ActiveIcon={Pin}
        isActive={isPinned}
        activeCls={activePin}
        testId={`pin-toggle-${type}-${id}`}
      />
      {showArchive && (
        <IconButton
          kind="archive"
          label={isArchived ? "Restore from archive" : "Move to archive"}
          Icon={Archive}
          ActiveIcon={Archive}
          isActive={isArchived}
          activeCls={activeArch}
          testId={`archive-toggle-${type}-${id}`}
        />
      )}
    </div>
  );
}
